import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faUsers, faSignal, faCashRegister, faChevronDown, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FaChevronDown } from 'react-icons/fa';

// Dropdown options data
const businessTypeOptions = [
  { value: 'Restaurant', label: 'Restaurant' },
  // Directly under Restaurant so the pair is seen together — a venue that
  // runs a function hall should not have to scroll past twelve other types
  // to discover the option exists. Choosing it is what switches the banquet
  // lane on: it resolves to its own BusinessTypes row, whose has_banquet
  // flag the backend reads (utils/banquetFlag.js).
  { value: 'Restaurant + Banquet', label: 'Restaurant + Banquet' },
  { value: 'Cafe', label: 'Cafe' },
  { value: 'Pub / Bar', label: 'Pub / Bar' },
  { value: 'Fast Food Outlet (QSR)', label: 'Fast Food Outlet (QSR)' },
  { value: 'Bakery', label: 'Bakery' },
  { value: 'Ice Cream Parlor', label: 'Ice Cream Parlor' },
  { value: 'Sweet Shop', label: 'Sweet Shop' },
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Tiffin Center (or Tiffin Hotel)', label: 'Tiffin Center (or Tiffin Hotel)' },
  { value: 'Cloud Kitchen', label: 'Cloud Kitchen' },
  { value: 'Catering Service', label: 'Catering Service' },
  { value: 'Food Truck / Mobile Vendor', label: 'Food Truck / Mobile Vendor' },
  { value: 'Juice Bar', label: 'Juice Bar' }
];

const machineTypeOptions = [
  { value: 'Inventory', label: 'Inventory' },
  { value: 'QR + Inventory', label: 'QR + Inventory' }
];

const isPlaceholderBusinessName = (name) => {
  if (!name) return true;
  return /^.+'s Business \([^)]+@[^)]+\)$/i.test(String(name).trim());
};

const resolveApiUrl = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (process.env.NODE_ENV === 'development') {
    return `/api${cleanEndpoint}`;
  }
  const base = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'https://be.invexis.co.in/api').replace(/\/+$/, '');
  const normalizedBase = base.endsWith('/api') ? base : `${base}/api`;
  return `${normalizedBase}${cleanEndpoint}`;
};

const getDraftKey = () => {
  try {
    const bizId = localStorage.getItem('businessId');
    if (bizId) return `onboarding_profile_draft_${bizId}`;
  } catch (_) {}
  return 'onboarding_profile_draft';
};

const getSavedDraft = () => {
  try {
    let draft = {};
    const globalRaw = localStorage.getItem('onboarding_profile_draft');
    if (globalRaw) {
      try {
        const parsed = JSON.parse(globalRaw);
        if (parsed && typeof parsed === 'object') {
          draft = { ...draft, ...parsed };
        }
      } catch (_) {}
    }
    const bizId = localStorage.getItem('businessId');
    if (bizId) {
      const bizRaw = localStorage.getItem(`onboarding_profile_draft_${bizId}`);
      if (bizRaw) {
        try {
          const parsed = JSON.parse(bizRaw);
          if (parsed && typeof parsed === 'object') {
            draft = { ...draft, ...parsed };
          }
        } catch (_) {}
      }
    }
    const storedBizName = localStorage.getItem('businessName');
    if (storedBizName && !draft.businessName && !isPlaceholderBusinessName(storedBizName)) {
      draft.businessName = storedBizName;
    }
    return draft;
  } catch (e) {
    console.error('Error reading onboarding draft:', e);
    return {};
  }
};

const saveDraftToStorage = (updates) => {
  try {
    const existing = getSavedDraft() || {};
    const next = { ...existing, ...updates };
    const raw = JSON.stringify(next);
    localStorage.setItem('onboarding_profile_draft', raw);
    const bizId = localStorage.getItem('businessId');
    if (bizId) {
      localStorage.setItem(`onboarding_profile_draft_${bizId}`, raw);
    }
    if (next.businessName && !isPlaceholderBusinessName(next.businessName)) {
      localStorage.setItem('businessName', next.businessName);
    }
  } catch (e) {
    console.error('Error saving onboarding draft:', e);
  }
};

const OnboardingPage = ({ onContinue }) => {
  const savedDraft = useMemo(() => getSavedDraft() || {}, []);

  const [userName, setUserName] = useState(() => savedDraft.userName || '');
  const [businessName, setBusinessName] = useState(() => savedDraft.businessName || '');
  const [phoneNumber, setPhoneNumber] = useState(() => savedDraft.phoneNumber || '');
  const [businessType, setBusinessType] = useState(() => savedDraft.businessType || '');
  const [machineType, setMachineType] = useState(() => savedDraft.machineType || '');
  const [languageName, setLanguageName] = useState('');
  const [progress, setProgress] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);

  // Remove global safe-area body padding for this full-bleed onboarding screen
  useEffect(() => {
    document.body.classList.add('onboarding-fullbleed');
    return () => document.body.classList.remove('onboarding-fullbleed');
  }, []);

  // Fetch business details from backend on mount
  useEffect(() => {
    const fetchBusinessDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(resolveApiUrl('/auth/user'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.business) {
            setDataFetched(true);
            if (data.business.businessId) {
              try {
                localStorage.setItem('businessId', String(data.business.businessId));
              } catch (_) {}
            }
          }

          const draft = getSavedDraft() || {};

          // Hydrate fields: Priority is draft > current state > non-placeholder backend data
          const resolvedUserName = draft.userName || userName || data.name || '';

          const backendBizName = data.business?.businessName;
          const validBackendBizName = backendBizName && !isPlaceholderBusinessName(backendBizName) ? backendBizName : '';
          const resolvedBusinessName = draft.businessName || businessName || validBackendBizName || '';

          const resolvedPhoneNumber = draft.phoneNumber || phoneNumber || data.phoneNumber || '';

          const backendBizType = data.business?.onboardingBusinessType || data.business?.businessTypeName;
          let matchedBizType = '';
          if (backendBizType && backendBizType !== 'Other') {
            const matched = businessTypeOptions.find(opt => opt.value.toLowerCase() === backendBizType.toLowerCase());
            if (matched) matchedBizType = matched.value;
          }
          const resolvedBusinessType = draft.businessType || businessType || matchedBizType || '';

          const backendBillingModel = data.business?.onboardingMachineType || data.business?.billingModelName;
          let matchedMachine = '';
          if (backendBillingModel) {
            const lowerModel = backendBillingModel.toLowerCase();
            matchedMachine = lowerModel.includes('qr') ? 'QR + Inventory' : 'Inventory';
          }
          const resolvedMachineType = draft.machineType || machineType || matchedMachine || '';

          if (resolvedUserName) setUserName(resolvedUserName);
          if (resolvedBusinessName) setBusinessName(resolvedBusinessName);
          if (resolvedPhoneNumber) setPhoneNumber(resolvedPhoneNumber);
          if (resolvedBusinessType) setBusinessType(resolvedBusinessType);
          if (resolvedMachineType) setMachineType(resolvedMachineType);

          // Save resolved draft to storage cleanly in one call
          saveDraftToStorage({
            userName: resolvedUserName,
            businessName: resolvedBusinessName,
            phoneNumber: resolvedPhoneNumber,
            businessType: resolvedBusinessType,
            machineType: resolvedMachineType
          });
        }
      } catch (error) {
        console.error('Error fetching business details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessDetails();
  }, []);

  // Custom dropdown states (from map1.js)
  const [dropdowns, setDropdowns] = useState({});
  const [searchTerms, setSearchTerms] = useState({});

  useEffect(() => {
    const totalFields = 5;
    let completedFields = 0;
    if (userName) completedFields++;
    if (businessName) completedFields++;
    if (phoneNumber && phoneNumber.length === 10) completedFields++;
    if (businessType) completedFields++;
    if (machineType) completedFields++;

    setProgress((completedFields / totalFields) * 100);
    setCompletedCount(completedFields);
  }, [userName, businessName, phoneNumber, businessType, machineType]);

  // Dropdown functions (from map1.js)
  const toggleDropdown = (type) => {
    setDropdowns(prev => {
      // If this dropdown is already open, close it
      if (prev[type] === type) {
        return { ...prev, [type]: null };
      }
      // Otherwise, close all dropdowns and open this one
      return { businessType: null, machineType: null, [type]: type };
    });
    // Removed auto-scrolling to keep page position fixed when opening dropdowns
  };

  const handleSelect = (type, value) => {
    if (type === 'businessType') {
      setBusinessType(value);
      saveDraftToStorage({ businessType: value });
    } else if (type === 'machineType') {
      setMachineType(value);
      saveDraftToStorage({ machineType: value });
    }
    setDropdowns(prev => ({ ...prev, [type]: null }));
  };

  const handleSearchChange = (type, value) => {
    setSearchTerms(prev => ({ ...prev, [type]: value }));
  };

  const filteredOptions = (type, search) => {
    const options = type === 'businessType' ? businessTypeOptions : machineTypeOptions;
    return options.filter(option =>
      option.label.toLowerCase().includes((search || '').toLowerCase())
    );
  };

  // Get dropdown position
  const getDropdownPosition = (element) => {
    if (!element) return {};
    const rect = element.getBoundingClientRect();
    return {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width
    };
  };

  // Close dropdown when clicking outside (from map1.js)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown-wrapper')) {
        setDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleContinue = async () => {
    // Validate phone number
    if (phoneNumber && phoneNumber.length !== 10) {
      alert('Phone number must be exactly 10 digits');
      return;
    }

    if (userName && businessName && phoneNumber && businessType && machineType) {
      // Save draft immediately to ensure preservation
      saveDraftToStorage({
        userName,
        businessName,
        phoneNumber,
        businessType,
        machineType
      });
      try {
        localStorage.setItem('businessName', businessName);
      } catch (_) {}

      // Check if QR billing was selected
      const isQRBilling = machineType.toLowerCase().includes('qr');

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          alert('Please Log In Again');
          return;
        }

        const data = {
          userName,
          businessName,
          phoneNumber,
          businessType,
          machineType
        };

        const response = await fetch(resolveApiUrl('/auth/complete-onboarding'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(data)
        });

        const result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to save onboarding data');
        }

        // Cache user & business updates in localStorage
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          storedUser.name = userName;
          storedUser.phoneNumber = phoneNumber;
          if (!storedUser.business) storedUser.business = {};
          storedUser.business.businessName = businessName;
          localStorage.setItem('user', JSON.stringify(storedUser));
        } catch (_) {}

      } catch (error) {
        console.error('❌ Onboarding save error:', error);
        alert(`Failed to save onboarding data: ${error.message}`);
        return;
      }

      if (onContinue) {
        onContinue({ userName, businessName, phoneNumber, businessType, machineType, isQRBilling });
      }
    } else {
      alert('Please Fill All Fields');
    }
  };

  if (loading) {
    return (
      <div className="onboarding-container-1">
        <div className="header-content">
          <h1 className="main-heading">Invexis</h1>
          <p className="sub-heading">Loading your business details...</p>
        </div>
        <div className="centered-content" style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ color: '#666', fontSize: '16px' }}>Please wait...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="onboarding-container-1">
        <div className="onboarding-card">
          <div className="onboarding-header-content">
            <h1 className="onboarding-brand-logo-text">INVEXIS</h1>
            <p className="onboarding-sub-heading">
              {dataFetched 
                ? "Your business profile is ready! Let's continue setup." 
                : "Help us customize Invexis for your specific needs"}
            </p>
          </div>
          <div className="onboarding-progress-wrapper">
            <div className="onboarding-progress-track">
              <div
                className="onboarding-progress-indicator"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
          <p className="progress-text">{completedCount} out of 5 completed</p>
          <div className="form-content">
            <div>
                <h2 className="onboarding-heading">
                  Name of the User
                </h2>
                <input
                  type="text"
                  placeholder="Enter Your Full Name"
                  value={userName}
                  onChange={(e) => {
                    setUserName(e.target.value);
                    saveDraftToStorage({ userName: e.target.value });
                  }}
                  className="onboarding-user-name-input"
                />
              </div>

              <div id="businessTypeDropdownWrapper">
                <h2 className="onboarding-heading">
                  Name of the Business
                </h2>
                <input
                  type="text"
                  placeholder="Enter Your Business Name"
                  value={businessName}
                  onChange={(e) => {
                    setBusinessName(e.target.value);
                    saveDraftToStorage({ businessName: e.target.value });
                  }}
                  className="onboarding-business-name-input"
                />
              </div>

              <div id="machineTypeDropdownWrapper">
                <h2 className="onboarding-heading">Phone Number</h2>
                <div className="phone-input-wrapper">
                  <input
                    type="tel"
                    placeholder="Enter Your Phone Number"
                    value={phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      if (value.length <= 10) {
                        setPhoneNumber(value);
                        saveDraftToStorage({ phoneNumber: value });
                      }
                    }}
                    className="onboarding-phone-input"
                    maxLength="10"
                  />
                  {phoneNumber && phoneNumber.length !== 10 && (
                    <span style={{
                      color: '#e53935',
                      fontSize: '12px',
                      marginTop: '4px',
                      display: 'block'
                    }}>
                      Phone number must be exactly 10 digits
                    </span>
                  )}
                </div>
              </div>

              <div>
                <h2 className="onboarding-heading">
                  Business Type
                </h2>
                <div className="custom-dropdown-wrapper">
                  <div
                    onClick={() => toggleDropdown('businessType')}
                    className="onboarding-business-type-dropdown"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      opacity: 1
                    }}
                  >
                    <span>{businessType || 'Choose your business type'}</span>
                    <FaChevronDown style={{ fontSize: '14px', color: '#666' }} />
                  </div>

                  {dropdowns.businessType && (
                    <div
                      className="custom-dropdown-content"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 9999
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Search business type..."
                        value={searchTerms.businessType || ''}
                        onChange={(e) => handleSearchChange('businessType', e.target.value)}
                        className="dropdown-search-input"
                      />
                      <div className="dropdown-options-container">
                        {filteredOptions('businessType', searchTerms.businessType).length === 0 ? (
                          <div className="no-options-message">
                            No business types match your search
                          </div>
                        ) : (
                          filteredOptions('businessType', searchTerms.businessType).map((option, i) => (
                            <div
                              key={i}
                              onClick={() => handleSelect('businessType', option.value)}
                              className="dropdown-option-item"
                              style={{
                                backgroundColor: businessType === option.value ? 'whitesmoke' : 'white',
                                color: businessType === option.value ? 'black' : '#000'
                              }}
                            >
                              <div>{option.label}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h2 className="onboarding-heading">
                  Business Services
                </h2>
                <div className="custom-dropdown-wrapper">
                  <div
                    onClick={() => toggleDropdown('machineType')}
                    className="onboarding-business-services-dropdown"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      opacity: 1
                    }}
                  >
                    <span>{machineType ? machineTypeOptions.find(opt => opt.value === machineType)?.label : 'Choose your business services'}</span>
                    <FaChevronDown style={{ fontSize: '14px', color: '#666' }} />
                  </div>

                  {dropdowns.machineType && (
                    <div
                      className="custom-dropdown-content"
                      style={{
                        top: 'auto',
                        bottom: '100%',
                        marginTop: 0,
                        marginBottom: '8px'
                      }}
                    >
                      <div className="dropdown-options-container">
                        {filteredOptions('machineType', searchTerms.machineType).map((option, i) => (
                            <div
                              key={i}
                              onClick={() => handleSelect('machineType', option.value)}
                              className="dropdown-option-item"
                              style={{
                                backgroundColor: machineType === option.value ? 'whitesmoke' : 'white',
                                color: machineType === option.value ? 'black' : '#000'
                              }}
                            >
                              <div>{option.label}</div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
          </div>
          <div className="button-container-inline">
            <button onClick={handleContinue}>Continue</button>
          </div>
        </div>
      </div>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;800&display=swap');
          .onboarding-container-1 {
            max-width: 100%;
            width: 100%;
            margin: 0;
            padding: 0; /* remove outer spacing so card touches top/bottom */
            font-family: 'Roboto', Arial, sans-serif;
            background: transparent; /* let card be the only visible surface */
            min-height: 100vh;
            height: auto;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            box-sizing: border-box;
            position: relative;
          }

          .onboarding-header-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-bottom: 20px;
            width: 100%;
          }

          .main-heading {
            font-size: 2.5rem;
            margin: 0;
            font-family: 'Roboto', Arial, sans-serif;
          }

          .onboarding-brand-logo-text {
            color: #000 !important;
            font-family: 'Roboto', Arial, sans-serif;
            font-weight: 800;
            font-size: 32px;
            letter-spacing: 2px;
            line-height: 1.2;
            margin: 0 0 12px 0;
            user-select: none;
            width: 100%;
          }

          .onboarding-sub-heading {
            color: #666;
            margin: 0;
            font-size: 16px;
            line-height: 1.5;
            width: 100%;
          }

          .onboarding-progress-wrapper {
            width: 100%;
            display: flex;
            justify-content: center;
          }

          .onboarding-progress-track {
            width: 100%;
            max-width: 500px;
            height: 0.5rem;
            background: #f3f4f6;
            border-radius: 8px;
            overflow: hidden;
            border: 1px solid #e5e7eb;
            position: relative;
          }

          .onboarding-progress-indicator {
            height: 100%;
            background: linear-gradient(90deg, #000, #666);
            background-size: 200% 100%;
            border-radius: 8px;
            transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .progress-text {
            text-align: center;
            color: #666;
            font-size: 0.85rem;
            margin: 6px 0 18px 0; /* tighter bottom space */
          }

          .centered-content {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            /* No scrolling here */
          }

          .form-content {
            width: 100%;
            text-align: left;
            padding: 0 4px 0 4px;
            border-radius: 0;
          }

          /* Unified card */
          .onboarding-card {
            background: #fff;
            --invexis-input-bg: #fff;
            border-radius: 0; /* full-bleed top and bottom */
            box-shadow: none; /* remove shadow so it feels like full page card */
            padding: 32px 36px 40px 36px; /* internal spacing now that outer padding removed */
            margin: 0 auto;
            width: 100%; /* full width by default */
            max-width: 600px; /* max width for desktop */
            box-sizing: border-box;
            min-height: 100vh; /* stretch card to full viewport height */
            height: auto;
            flex-shrink: 0;
            display: flex;
            flex-direction: column;
          }
          
          /* Desktop view - apply 600px fixed width */
          @media screen and (min-width: 769px) {
            .onboarding-card {
              width: 600px;
              max-width: 600px;
            }
          }
          
          /* Mobile view - full width */
          @media screen and (max-width: 768px) {
            .onboarding-card {
              width: 100% !important;
              max-width: 100% !important;
              padding: 20px 16px 28px 16px;
            }
            
            .form-content {
              padding: 0 !important;
              flex-grow: 1;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
            }
            
            .onboarding-header-content {
              padding: 0;
            }
            
            .onboarding-progress-wrapper {
              padding: 0;
            }
            
            .progress-text {
              padding: 0;
            }
            
            .button-container-inline {
              margin-top: auto;
              padding: 0;
              padding-top: 16px;
            }
            
            button {
              margin-top: 0;
            }
            
            .onboarding-brand-logo-text {
              font-size: 28px;
              margin: 0 0 10px 0;
            }
            
            .onboarding-sub-heading {
              font-size: 14px;
              margin: 0;
            }
            
            .onboarding-heading {
              font-size: 1.1rem;
              padding: 0.25rem 0 0.1rem 0;
            }
            
            .progress-bar-container {
              max-width: 100%;
            }
            
            html body .onboarding-card .onboarding-user-name-input,
            html body .onboarding-card .onboarding-business-name-input,
            html body .onboarding-card .onboarding-phone-input {
              height: 60px !important;
              padding: 18px 10px !important;
              font-size: 16px !important;
            }
            
            html body .onboarding-card .onboarding-business-type-dropdown,
            html body .onboarding-card .onboarding-business-services-dropdown {
              height: 60px !important;
              padding: 18px 10px !important;
            }
          }
          
          /* Small phones */
          @media screen and (max-width: 480px) {
            .onboarding-card {
              width: 100% !important;
              max-width: 100% !important;
              padding: 18px 14px 24px 14px;
            }
            
            .form-content {
              padding: 0 !important;
              flex-grow: 1;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
            }
            
            .onboarding-header-content {
              padding: 0;
            }
            
            .onboarding-progress-wrapper {
              padding: 0;
            }
            
            .progress-text {
              padding: 0;
            }
            
            .button-container-inline {
              margin-top: auto;
              padding: 0;
              padding-top: 14px;
            }
            
            button {
              margin-top: 0;
            }
            
            .onboarding-brand-logo-text {
              font-size: 26px;
              margin: 0 0 8px 0;
            }
            
            .onboarding-sub-heading {
              font-size: 13px;
            }
            
            .onboarding-heading {
              font-size: 1rem;
            }
            
            html body .onboarding-card .onboarding-user-name-input,
            html body .onboarding-card .onboarding-business-name-input,
            html body .onboarding-card .onboarding-phone-input {
              height: 60px !important;
              padding: 18px 10px !important;
              font-size: 16px !important;
            }
            
            html body .onboarding-card .onboarding-business-type-dropdown,
            html body .onboarding-card .onboarding-business-services-dropdown {
              height: 60px !important;
              padding: 18px 10px !important;
            }
          }

          .onboarding-heading {
            color: #000000ff;
            margin-bottom: 6px; /* reduced from 10px */
            font-size: 1.3rem; /* slightly smaller */
            line-height: 22px;
            padding: 0.75rem 0 0.15rem 0; /* reduced vertical padding */
            display: flex;
            align-items: center;
          }
          
          .heading-icon {
            margin-right: 12px;
            color: #000000ff;
            font-size: 1.25rem;
          }

          /* Custom Dropdown Styles - Exact copy from map1.js */
          .custom-dropdown-wrapper {
            position: relative;
            width: 100%;
            margin-bottom: 10px;
          }

          .custom-dropdown-trigger {
            border: 1px solid #ddd;
            cursor: pointer;
            background-color: #fff;
            color: black;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px;
            border-radius: 8px;
            transition: border 0.2s ease-in-out;
            width: 100%;
            height: 44px;
            box-sizing: border-box;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }

          .custom-dropdown-content {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            z-index: 9999;
            background-color: #fff;
            border-radius: 12px;
            box-shadow: 0 4px 16px rgba(0,0,0,0.2);
            padding: 10px;
            margin-top: 8px;
            max-height: 240px;
            overflow: hidden;
          }

          .dropdown-search-input {
            width: 100%;
            padding: 10px;
            border-radius: 8px;
            border: 1px solid #ddd;
            margin-bottom: 8px;
            box-sizing: border-box;
          }

          .dropdown-options-container {
            max-height: 150px;
            overflow-y: auto;
          }

          /* Hide scrollbar for dropdown */
          .dropdown-options-container::-webkit-scrollbar {
            display: none;
          }
          .dropdown-options-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          .dropdown-option-item {
            padding: 10px;
            border-radius: 8px;
            cursor: pointer;
          }

          .dropdown-option-item:hover {
            background-color: #f5f5f5 !important;
          }

          .no-options-message {
            padding: 20px;
            text-align: center;
            color: #666;
            font-size: 14px;
          }
          /* HIGH PRIORITY ONBOARDING INPUT STYLES */
          html body .onboarding-card .onboarding-user-name-input,
          html body .onboarding-card .onboarding-business-name-input,
          html body .onboarding-card .onboarding-phone-input {
            width: 100% !important;
            padding: 10px !important;
            border: 1px solid #d1d5db !important;
            border-radius: 8px !important;
            font-size: 16px !important;
            box-sizing: border-box !important;
            font-family: inherit !important;
            transition: border 0.2s ease-in-out !important;
            background-color: #fff !important;
            background-image: none !important;
            color: #000 !important;
            height: 44px !important;
            cursor: text !important;
          }

          /* Autofill override - block Chrome blue background */
          html body .onboarding-card .onboarding-user-name-input:-webkit-autofill,
          html body .onboarding-card .onboarding-user-name-input:-webkit-autofill:hover,
          html body .onboarding-card .onboarding-user-name-input:-webkit-autofill:focus,
          html body .onboarding-card .onboarding-user-name-input:-webkit-autofill:active,
          html body .onboarding-card .onboarding-business-name-input:-webkit-autofill,
          html body .onboarding-card .onboarding-business-name-input:-webkit-autofill:hover,
          html body .onboarding-card .onboarding-business-name-input:-webkit-autofill:focus,
          html body .onboarding-card .onboarding-business-name-input:-webkit-autofill:active,
          html body .onboarding-card .onboarding-phone-input:-webkit-autofill,
          html body .onboarding-card .onboarding-phone-input:-webkit-autofill:hover,
          html body .onboarding-card .onboarding-phone-input:-webkit-autofill:focus,
          html body .onboarding-card .onboarding-phone-input:-webkit-autofill:active {
            background-color: #fff !important;
            -webkit-box-shadow: 0 0 0 1000px #fff inset !important;
            box-shadow: 0 0 0 1000px #fff inset !important;
            -webkit-text-fill-color: #000 !important;
            caret-color: #000 !important;
            border: 1px solid #d1d5db !important;
            transition: background-color 999999s ease-out 0s !important;
          }

          html body .onboarding-card .onboarding-user-name-input:-moz-autofill,
          html body .onboarding-card .onboarding-business-name-input:-moz-autofill,
          html body .onboarding-card .onboarding-phone-input:-moz-autofill {
            box-shadow: 0 0 0 1000px #fff inset !important;
            background-color: #fff !important;
            color: #000 !important;
          }

          html body .onboarding-card .onboarding-business-type-dropdown,
          html body .onboarding-card .onboarding-business-services-dropdown {
            border: 1px solid #d1d5db !important;
            cursor: pointer !important;
            background-color: #fff !important;
            color: black !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            padding: 10px !important;
            border-radius: 8px !important;
            transition: border 0.2s ease-in-out !important;
            width: 100% !important;
            height: 44px !important;
            box-sizing: border-box !important;
          }

          /* Unified Input Field Styles - Matching dropdown exactly */
          .unified-input-field {
            width: 100%;
            padding: 10px;
            border: 1px solid #ccc;
            border-radius: 8px;
            font-size: 16px;
            box-sizing: border-box;
            font-family: inherit;
            transition: border 0.2s ease-in-out;
            background-color: #fff;
            color: #000;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            height: 44px;
            cursor: text;
          }

          .unified-input-field:focus {
            outline: none;
            border: 1px solid black;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }

          /* Phone number specific stronger border */
          .phone-input-field {
            border: 1px solid #000 !important;
          }
          .phone-input-field:focus {
            border: 1px solid #000 !important;
          }

          .unified-input-field::placeholder {
            color: #999;
            font-size: 15px;
          }

          /* Phone Input Wrapper */
          .phone-input-wrapper {
            position: relative;
            width: 100%;
            margin-bottom: 0;
          }

          /* Removed phone-number-card; phone input now matches standard inputs */


          .button-container-inline {
            margin-top: 24px;
            width: 100%;
            box-sizing: border-box;
          }
          .button-container-inline button {
            width: 100%;
            font-size: 18px;
            padding: 16px 0;
            border-radius: 12px;
          }

          button {
            width: 100%;
            padding: 15px;
            background-color: #000;
            color: #fff;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          button:hover {
            background-color: #1a1a1a;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          button:active {
            transform: scale(0.99);
          }
          /* Thin scrollbar for form-content */
          .form-content::-webkit-scrollbar {
            width: 7px;
            background: #f0f0f0;
            border-radius: 8px;
          }
          .form-content::-webkit-scrollbar-thumb {
            background: #bbb;
            border-radius: 8px;
          }
          .form-content::-webkit-scrollbar-thumb:hover {
            background: #888;
          }
          .form-content {
            scrollbar-width: thin;
            scrollbar-color: #bbb #f0f0f0;
          }
          .worker-count-input::-webkit-inner-spin-button,
          .worker-count-input::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .worker-count-input {
            -moz-appearance: textfield;
            appearance: textfield;
            outline: none !important;
            box-shadow: none !important;
          }
          .worker-count-input::-webkit-inner-spin-button,
          .worker-count-input::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
        
          }
          .worker-count-input {
            -moz-appearance: textfield;
            appearance: textfield;
            outline: none !important;
          }
          .form-card {
            background: #fff;
            border-radius: 18px;
            box-shadow: 0 4px 24px 0 rgba(0,0,0,0.10), 0 1.5px 4px 0 rgba(0,0,0,0.08);
            padding: 24px 20px 20px 20px;
            margin-bottom: 20px;
          }
          
          /* Tablet and below - reduce form-card padding */
          @media screen and (max-width: 768px) {
            .form-card {
              padding: 16px 12px 16px 12px;
              margin-bottom: 16px;
              border-radius: 12px;
            }
            .onboarding-heading{
              padding:0 0 0.15rem 0;
            }
          }
          
          /* Small phones - minimal form-card padding */
          @media screen and (max-width: 480px) {
            .form-card {
              padding: 12px 8px 12px 8px;
              margin-bottom: 12px;
              border-radius: 10px;
            }
              .onboarding-heading{
              padding:0 0 0.15rem 0;
            }
          }
          
          /* MOBILE HEIGHT OVERRIDES - Must be last to override base styles */
          @media screen and (max-width: 768px) {
            html body .onboarding-card .onboarding-user-name-input,
            html body .onboarding-card .onboarding-business-name-input,
            html body .onboarding-card .onboarding-phone-input {
              height: 50px !important;
              padding: 13px 10px !important;
            }
            
            html body .onboarding-card .onboarding-business-type-dropdown,
            html body .onboarding-card .onboarding-business-services-dropdown {
              height: 50px !important;
              padding: 13px 10px !important;
            }
          }
          
          @media screen and (max-width: 480px) {
            html body .onboarding-card .onboarding-user-name-input,
            html body .onboarding-card .onboarding-business-name-input,
            html body .onboarding-card .onboarding-phone-input {
              height: 50px !important;
              padding: 13px 10px !important;
            }
            
            html body .onboarding-card .onboarding-business-type-dropdown,
            html body .onboarding-card .onboarding-business-services-dropdown {
              height: 50px !important;
              padding: 13px 10px !important;
            }
          }
          
          body {
            background: #F7F8FA !important;
          }
        `}
      </style>
    </>
  );
};

export default OnboardingPage;
